import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2, ArrowLeft } from "lucide-react";
import { formatDetail } from "@/lib/format";
import TwoFactor from "@/components/TwoFactor";

const GoogleIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden>
    <path fill="#fff" d="M21.35 11.1H12v3.8h5.35c-.25 1.5-1.7 4.4-5.35 4.4-3.2 0-5.85-2.65-5.85-5.9s2.65-5.9 5.85-5.9c1.85 0 3.05.8 3.75 1.45l2.55-2.5C16.7 3.8 14.6 2.9 12 2.9 6.95 2.9 2.9 6.95 2.9 12s4.05 9.1 9.1 9.1c5.25 0 8.7-3.7 8.7-8.9 0-.6-.05-1.05-.15-1.5Z" />
  </svg>
);

export default function Login() {
  const { refetch } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [mode, setMode] = useState("choice"); // choice | signin | register | forgot
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [twofa, setTwofa] = useState(null);

  useEffect(() => {
    api.get("/signin-config").then((r) => setCfg(r.data)).catch(() => setCfg({}));
  }, []);

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get("join") === "1") {
      setMode("register");
      setForm((f) => ({ ...f, email: p.get("email") || "", name: p.get("name") || "" }));
    }
  }, []);

  const c = cfg || {};
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setNotice(""); setBusy(true);
    try {
      if (mode === "signin") {
        const r = await api.post("/auth/login", { email: form.email, password: form.password });
        if (r.data?.twofa_required) { setTwofa(r.data); return; }
        await refetch();
      } else if (mode === "register") {
        const r = await api.post("/auth/register", { email: form.email, password: form.password, name: form.name });
        if (r.data?.twofa_required) { setTwofa(r.data); return; }
        await refetch();
      } else if (mode === "forgot") {
        const r = await api.post("/auth/forgot-password", { email: form.email });
        setNotice(r.data.message || c.forgot_success);
      }
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(formatDetail(d));
      if (d && d.code === "use_google") setMode("choice");
    } finally {
      setBusy(false);
    }
  };

  const goto = (m) => { setMode(m); setError(""); setNotice(""); };

  const inputCls = "w-full bg-warm-cream border border-ink/20 rounded-xl px-4 py-3 min-h-[50px] text-[16px] focus:bg-white";

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-warm-cream">
      <div className="lg:w-[46%] bg-ink text-cream flex flex-col justify-between p-8 lg:p-14 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #daa405 0, transparent 40%), radial-gradient(circle at 80% 70%, #808c20 0, transparent 40%)" }} />
        <div className="relative z-10 flex items-center gap-3">
          <img src="/logo.png" alt="Rosemoor" className="w-14 h-14" />
          <div>
            <div className="font-zilla font-semibold text-xl leading-none">Rosemoor</div>
            <div className="text-sm tracking-widest uppercase text-gold">Membership Portal</div>
          </div>
        </div>
        <div className="relative z-10 max-w-md">
          <h1 className="font-zilla text-4xl sm:text-5xl font-semibold leading-tight mb-5">{c.hero_headline || "One home for the whole association."}</h1>
          <p className="text-lg text-cream/80 leading-relaxed">{c.hero_subtext || "Members, staff, and board all sign in here. Rosemoor reads your account and takes you straight to what you need."}</p>
        </div>
        <div className="relative z-10 text-sm text-cream/55">{c.hero_footer || "Rosemoor Community Association · Chicago's Far South Side since 1941"}</div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md animate-rise">
          <div className="bg-cream border border-ink/15 rounded-2xl p-8 shadow-[0_20px_50px_-25px_rgba(20,20,15,0.4)]">
            <div className="flex justify-center mb-5">
              <img src="/logo-full.png" alt="Rosemoor Community Association" className="h-14" />
            </div>

            {!cfg ? (
              <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-maroon" /></div>
            ) : twofa ? (
              <TwoFactor data={twofa} onComplete={refetch} />
            ) : (
              <>
                {mode !== "choice" && (
                  <button onClick={() => goto("choice")} data-testid="back-to-choice" className="flex items-center gap-1.5 text-[14px] text-ink/60 mb-3 hover:text-ink">
                    <ArrowLeft className="w-4 h-4" /> Back
                  </button>
                )}

                <h2 className="font-zilla text-3xl font-semibold mb-1">
                  {mode === "choice" && c.headline}
                  {mode === "signin" && c.email_headline}
                  {mode === "register" && c.register_headline}
                  {mode === "forgot" && c.forgot_headline}
                </h2>
                <p className="text-ink/70 mb-6 text-[16px]">
                  {mode === "choice" && c.subtext}
                  {mode === "signin" && c.email_subtext}
                  {mode === "register" && c.register_subtext}
                  {mode === "forgot" && c.forgot_subtext}
                </p>

                {error && <div data-testid="auth-error" className="bg-maroon/10 border border-maroon/30 text-ink rounded-lg px-4 py-3 mb-4 text-[15px]">{error}</div>}
                {notice && <div data-testid="auth-notice" className="bg-olive/15 border border-olive/40 text-ink rounded-lg px-4 py-3 mb-4 text-[15px]">{notice}</div>}

                {mode === "choice" && (
                  <div className="space-y-3">
                    <button data-testid="google-signin-button" onClick={handleGoogle}
                      className="w-full flex items-center justify-center gap-3 bg-maroon text-cream font-semibold rounded-xl px-5 py-4 min-h-[52px] text-lg transition-transform hover:-translate-y-0.5">
                      <GoogleIcon /> {c.google_button}
                    </button>
                    {c.email_password_enabled && (
                      <>
                        <div className="flex items-center gap-3 my-2 text-ink/40 text-[13px]">
                          <span className="flex-1 h-px bg-ink/15" /> or <span className="flex-1 h-px bg-ink/15" />
                        </div>
                        <button data-testid="email-toggle-button" onClick={() => goto("signin")}
                          className="w-full border-[1.5px] border-ink/25 rounded-xl px-5 py-4 min-h-[52px] text-[16px] font-semibold hover:bg-ink/5">
                          {c.email_toggle}
                        </button>
                      </>
                    )}
                  </div>
                )}

                {(mode === "signin" || mode === "register") && c.email_password_enabled && (
                  <div className="grid grid-cols-2 gap-1 bg-warm-cream border border-ink/15 rounded-xl p-1 mb-5" data-testid="auth-mode-tabs">
                    <button type="button" data-testid="tab-signin" onClick={() => goto("signin")}
                      className={`rounded-lg py-2.5 text-[15px] font-semibold transition-colors ${mode === "signin" ? "bg-maroon text-cream" : "text-ink/60 hover:text-ink"}`}>
                      {c.signin_submit || "Sign in"}
                    </button>
                    <button type="button" data-testid="tab-register" onClick={() => goto("register")}
                      className={`rounded-lg py-2.5 text-[15px] font-semibold transition-colors ${mode === "register" ? "bg-maroon text-cream" : "text-ink/60 hover:text-ink"}`}>
                      {c.register_submit || "Create account"}
                    </button>
                  </div>
                )}

                {(mode === "signin" || mode === "register") && (
                  <form onSubmit={submit} className="space-y-3">
                    {mode === "register" && (
                      <div>
                        <label className="text-[14px] font-semibold">{c.name_field_label}</label>
                        <input data-testid="name-input" value={form.name} onChange={set("name")} required className={inputCls + " mt-1"} />
                      </div>
                    )}
                    <div>
                      <label className="text-[14px] font-semibold">{c.email_field_label}</label>
                      <input data-testid="email-input" type="email" value={form.email} onChange={set("email")} required className={inputCls + " mt-1"} />
                    </div>
                    <div>
                      <label className="text-[14px] font-semibold">{c.password_field_label}</label>
                      <input data-testid="password-input" type="password" value={form.password} onChange={set("password")} required minLength={8} className={inputCls + " mt-1"} />
                    </div>
                    <button data-testid="email-submit-button" type="submit" disabled={busy}
                      className="w-full flex items-center justify-center gap-2 bg-maroon text-cream font-semibold rounded-xl px-5 py-4 min-h-[52px] text-lg transition-transform hover:-translate-y-0.5 disabled:opacity-50">
                      {busy && <Loader2 className="w-5 h-5 animate-spin" />}
                      {mode === "signin" ? c.signin_submit : c.register_submit}
                    </button>
                    {mode === "signin" && (
                      <div className="flex items-center justify-end pt-1">
                        <button type="button" data-testid="to-forgot" onClick={() => goto("forgot")} className="text-[14px] text-ink/60 hover:underline">{c.forgot_link}</button>
                      </div>
                    )}
                    {mode === "register" && (
                      <p className="text-[13px] text-ink/55 pt-1" data-testid="register-hint">Pick any password (at least 8 characters). You'll use it with your email to sign in next time.</p>
                    )}
                  </form>
                )}

                {mode === "forgot" && (
                  <form onSubmit={submit} className="space-y-3">
                    <div>
                      <label className="text-[14px] font-semibold">{c.email_field_label}</label>
                      <input data-testid="forgot-email-input" type="email" value={form.email} onChange={set("email")} required className={inputCls + " mt-1"} />
                    </div>
                    <button data-testid="forgot-submit-button" type="submit" disabled={busy}
                      className="w-full flex items-center justify-center gap-2 bg-maroon text-cream font-semibold rounded-xl px-5 py-4 min-h-[52px] text-lg disabled:opacity-50">
                      {busy && <Loader2 className="w-5 h-5 animate-spin" />} {c.forgot_submit}
                    </button>
                  </form>
                )}

                <p className="mt-6 pt-5 border-t border-ink/12 text-[14px] text-ink/60">{c.footer_note}</p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
